import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { IPrepSettingsType, getPrepSettings } from '../gql'

export interface ISettingRule {
  locationGroupId: number | null
  locationId: number | null
  value: number
}

export interface ISettingSchedule {
  startDate: string
  endDate: string
  rules: ISettingRule[]
}

interface IPrepSetting {
  settingName: string
  settingRules: ISettingRule[]
  settingSchedules: ISettingSchedule[]
}

// why: an all-locations rule must lead the list — it is the one every other
// rule is read against, and the API returns rows in no particular order.
const byScope = (a: ISettingRule, b: ISettingRule) =>
  Number(a.locationGroupId !== null) - Number(b.locationGroupId !== null)

const usePrepSettings = () => {
  const { data, loading, refetch } =
    useQuery<IPrepSettingsType>(getPrepSettings)

  const prepSettings = useMemo((): IPrepSetting[] => {
    const activeData = data?.prepSettings?.nodes?.filter(
      (item) => item.deletedAt === null,
    )

    if (!activeData || activeData.length === 0) {
      return []
    }

    const settingsByName = activeData.reduce((acc, item) => {
      const name = item.name
      if (!acc[name]) {
        acc[name] = {
          settingName: name,
          settingRules: [],
          settingSchedules: [],
        }
      }

      const rule: ISettingRule = {
        locationGroupId: item.locationGroupId,
        locationId: item.locationId,
        value: item.value,
      }

      if (item.type === 'default') {
        acc[name].settingRules.push(rule)
      } else if (item.type === 'schedule') {
        // why: every rule is stored flat with its own dates, so the window the
        // rows share is what groups them back into one schedule.
        const startDate = item.startDate || ''
        const endDate = item.endDate || ''
        let schedule = acc[name].settingSchedules.find(
          (s) => s.startDate === startDate && s.endDate === endDate,
        )
        if (!schedule) {
          schedule = { startDate, endDate, rules: [] }
          acc[name].settingSchedules.push(schedule)
        }
        schedule.rules.push(rule)
      }

      return acc
    }, {} as Record<string, IPrepSetting>)

    Object.values(settingsByName).forEach((setting) => {
      setting.settingRules.sort(byScope)
      setting.settingSchedules.sort((a, b) =>
        a.startDate.localeCompare(b.startDate),
      )
      setting.settingSchedules.forEach((schedule) =>
        schedule.rules.sort(byScope),
      )
    })

    return Object.values(settingsByName)
  }, [data])

  return {
    prepSettings,
    loading,
    refetch,
  }
}

export default usePrepSettings
